import React, { Component, useContext, useEffect, useMemo, useState } from 'react';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import _ from 'lodash';
import ModelContextEditor from './ModelContextEditor';
import BadgeId from '../common/BadgeId';
import LegoContextSummary from './LegoContextSummary';
import { LocalesSelector } from './LocalesSelector';
import { LocalesList } from './LocalesList';
import LegoLabelsSelector from './LegoLabelsSelector';
import LegoLabel from './LegoLabel';
import { LabelsSummary } from '../data-issues/LabelsSummary';
import DataIssueStateSwitch from '../data-issues/DataIssueStateSwitch';
import { LegoStateBadge } from './LegoStateBadge';
import { OpenClosedIcon } from '../data-issues/OpenClosedIcon';
import { SwitchInput } from '../common/SwitchInput';
import { StateBadge } from '../data-issues/StateBadge';
import { LegoStateSwitch } from './LegoStateSwitch';
import MultiCountryEditor from '../common/editors/MultiCountryEditor';
import CountryFlag from '../common/CountryFlag';

function computeObjectsCommonFactor(objects) {
  let commonFactor = { ...objects[0] };
  for (let ctx of objects) {
    _.each(commonFactor, (v, k) => {
      if (!_.isEqual(ctx[k], v)) {
        delete commonFactor[k];
      }
    });
  }
  return commonFactor;
}

function computeArraysCommonFactor(arrays) {
  return _.intersectionWith(... arrays, _.isEqual);
}

function contextEntryMatchesFilter(entry, filter) {
  if (_.isEmpty(filter)) return true;
  return _.every(filter, (filterVal, key) => {
    if (filterVal && typeof filterVal === 'object' && !Array.isArray(filterVal) && filterVal.$regex !== undefined) {
      const re = new RegExp(filterVal.$regex, filterVal.$options || '');
      return typeof entry[key] === 'string' && re.test(entry[key]);
    }
    return _.isEqual(entry[key], filterVal);
  });
}

function computeCommonFactor(objects, columns, contextFilter = {}) {
  let commonFactor = {};
  for(let { dataField, logic } of columns) {
    let values = _.map(objects, dataField);

    if(logic === LOGIC_SET_UNSET__FIELD) {
      let unique = _.uniq(values);
      if(unique.length === 1 ) {
        commonFactor[dataField] = unique[0];
      }
    } else if (logic === LOGIC_ARRAY_SET_COMMON_FACTOR) {
      commonFactor[dataField] = computeArraysCommonFactor(values);
    } else if (logic === LOGIC_LEGO_CONTEXT) {
      const entries = _.flatMap(objects, dataField).filter(e => contextEntryMatchesFilter(e, contextFilter));
      commonFactor[dataField] = entries.length ? computeObjectsCommonFactor(entries) : {};
    }
  }
  return commonFactor;
}

export let LOGIC_SET_UNSET__FIELD = 'simple field';
export let LOGIC_ARRAY_SET_COMMON_FACTOR = 'additive_set';
export let LOGIC_LEGO_CONTEXT = 'lego context';


export const defaultColumns = {
  labels: {
    dataField: 'labels',
    valueEditor: (value, onChange) => <span style={{minWidth: '250px'}}><LegoLabelsSelector value={value} onChange={onChange}/></span>,
    valueDisplay: (value) => <LabelsSummary labels={value}/>,
    logic: LOGIC_ARRAY_SET_COMMON_FACTOR
  },

  issueState: {
    dataField: 'state',
    valueEditor: (value, onChange) => <DataIssueStateSwitch value={value} onChange={onChange}/>,
    valueDisplay: (value) => <StateBadge state={value}/>,
    logic: LOGIC_SET_UNSET__FIELD
  },

  legoState: {
    dataField: 'state',
    valueEditor: (value, onChange) => <LegoStateSwitch compact value={value} onChange={onChange}/>,
    valueDisplay: (value) => <LegoStateBadge state={value}/>,
    logic: LOGIC_SET_UNSET__FIELD
  },

  locales:     {
    dataField: 'locales',
    valueEditor: (value, onChange) => <LocalesSelector value={value} onChange={onChange}/>,
    valueDisplay: (value) => <LocalesList locales={value}/>,
    logic: LOGIC_ARRAY_SET_COMMON_FACTOR
  },

  country:     {
    dataField: 'country',
    valueEditor: (value, onChange) => <MultiCountryEditor value={value} onChange={onChange}/>,
    valueDisplay: (value) => (value || []).map(c => <CountryFlag countryCode={c}/>),
    logic: LOGIC_ARRAY_SET_COMMON_FACTOR
  },

  issueIsOpen: {
    dataField: 'isOpen',
    valueEditor: (isOpen, onChange) => <span>
       <span className={'align-middle'}>{isOpen ? 'Open issue' : 'Closed issue'}</span>
      <SwitchInput className={'mr-3 ml-2'} value={!isOpen} onChange={v => onChange(!v)}/>
    </span>,
    valueDisplay: (value) => <OpenClosedIcon isOpen={value} className={'mr-1'}/>,
    logic: LOGIC_SET_UNSET__FIELD
  },

  context: {
    dataField: 'context',
    valueEditor: (value, onChange) => <ModelContextEditor value={value} onChange={onChange}/>,
    valueDisplay: (value) => <LegoContextSummary context={value}/>,
    logic: LOGIC_LEGO_CONTEXT
  }
};


export default function ModalBatchEditDoc({ docs, onCancel, onSave, service, columns }) {
  let { page } = useContext(LegoAdminPageContext);

  columns = columns ? _.map(columns, c => _.isString(c) ? defaultColumns[c] : c) : _.values(defaultColumns);

  const contextColumns = useMemo(() => columns.filter(c => c.logic === LOGIC_LEGO_CONTEXT), [columns]);

  let [contextFilter, setContextFilter] = useState({});
  let fieldsCommonFactor = useMemo(() => computeCommonFactor(docs, columns, contextFilter), [contextFilter]);
  let [fieldsChange, setFieldsChange ] = useState(() => computeCommonFactor(docs, columns, {}));

  useEffect(() => {
    if (!contextColumns.length) return;
    setFieldsChange(prev => {
      const next = { ...prev };
      for (const { dataField } of contextColumns) next[dataField] = fieldsCommonFactor[dataField];
      return next;
    });
  }, [fieldsCommonFactor]);

  const predictChangedDocs = useMemo(() => docs.map(doc => {
    const colData = {};
    let rowHasDiff = false;

    for (const { dataField, logic } of columns) {
      const val = doc[dataField];

      if (logic === LOGIC_LEGO_CONTEXT) {
        const entries = (doc[dataField] || []).map(ctx => {
          const matches = contextEntryMatchesFilter(ctx, contextFilter);
          const contextPreview = matches ? { ..._.omit(ctx, _.keys(fieldsCommonFactor[dataField])), ... fieldsChange[dataField] } : ctx;
          const entryHasDiff = matches && !_.isEqual(ctx, contextPreview);
          rowHasDiff = rowHasDiff || entryHasDiff;
          const className = !matches ? 'opacity-50' : (entryHasDiff ? 'bg-light-warning' : '');
          return { contextPreview, className };
        });
        colData[dataField] = { logic, entries };
      } else {
        let predictedVal;
        if (logic === LOGIC_SET_UNSET__FIELD) {
          predictedVal = dataField in fieldsChange ? fieldsChange[dataField] : val;
        } else if (logic === LOGIC_ARRAY_SET_COMMON_FACTOR) {
          predictedVal = _.union(_.difference(val, fieldsCommonFactor[dataField]), fieldsChange[dataField]);
        }
        const isDiff = !_.isEqual(val, predictedVal);
        rowHasDiff = rowHasDiff || isDiff;
        const className = 'text-center ' + (isDiff ? 'bg-light-warning' : '');
        colData[dataField] = { logic, predictedVal, className };
      }
    }

    return { doc, rowHasDiff, colData };
  }), [docs, columns, contextFilter, fieldsCommonFactor, fieldsChange]);

  const affectedCount = predictChangedDocs.filter(d => d.rowHasDiff).length;

  const applyContextChangeToEntry = (entry, common, change) => {
    const updated = { ...entry };
    _.each(common, (v, k) => {
      if (change[k] === undefined) delete updated[k];
    });
    _.each(change, (v, k) => {
      if (!_.isEqual(common[k], v)) updated[k] = v;
    });
    return updated;
  };

  const createTasks = async () => {
    console.log('COMMON', fieldsCommonFactor)
    console.log('CHANGE', fieldsChange)
    console.log('CONTEXT FILTER', contextFilter)
    const $set = {};
    const $unset = {};
    let $pull = { };
    let $addToSet = { };
    let perDocContextUpdates = null;
    const filterActive = !_.isEmpty(contextFilter);

    for(let { dataField, logic } of columns) {
      let common = fieldsCommonFactor[dataField];
      let change = fieldsChange[dataField];

      if(logic === LOGIC_ARRAY_SET_COMMON_FACTOR) {
        const removed = _.difference(common, change);
        if(removed.length) {
          $pull[dataField] = {$in: removed};
        }
        const added = _.difference(change, common);
        if(added.length) {
          $addToSet[dataField] = added;
        }
      } else if (logic === LOGIC_SET_UNSET__FIELD) {
        if(dataField in fieldsChange && (!_.isEqual(common, change) || !(dataField in fieldsCommonFactor))) {
          if (change === undefined) {
            $unset[dataField] = true;
          } else {
            $set[dataField] = change;
          }
        }
      } else if (logic === LOGIC_LEGO_CONTEXT) {
        if(dataField in fieldsChange && (!_.isEqual(common, change) || !(dataField in fieldsCommonFactor))) {
          if (filterActive) {
            perDocContextUpdates = perDocContextUpdates || [];
            for (const doc of docs) {
              const newContext = (doc[dataField] || []).map(entry =>
                contextEntryMatchesFilter(entry, contextFilter) ? applyContextChangeToEntry(entry, common, change) : entry
              );
              perDocContextUpdates.push({ ids: doc.dataIssuesIds || [doc._id], dataField, newContext });
            }
          } else {
            _.each(common, (v, k) => {
              if (change[k] === undefined) {
                $unset['context.$[].' + k] = true;
              }
            })
            _.each(change, (v, k) => {
              if(!_.isEqual(common[k], v)) {
                $set['context.$[].' + k] = v;
              }
            })
          }
        }
      }
    }

    let updateFirstStep = {};
    if(!_.isEmpty($pull))
      updateFirstStep.$pull = $pull;

    if(!_.isEmpty($set))
      updateFirstStep.$set = $set;

    if(!_.isEmpty($unset))
      updateFirstStep.$unset = $unset;

    // Do update in two steps as mongo would fail with conflict if pull and addToSet used on same field on same object
    let updateSecondStep = {};
    if(!_.isEmpty($addToSet))
      updateSecondStep.$addToSet = $addToSet;

    let ids = _.flatMap(docs, doc => doc.dataIssuesIds || [doc._id]);
    const hasFirst = !_.isEmpty(updateFirstStep);
    const hasSecond = !_.isEmpty(updateSecondStep);
    const hasPerDoc = perDocContextUpdates && perDocContextUpdates.length;

    page.runAsync(async () => {
      if (hasPerDoc) {
        const signature = page.getLoggedUserSignature();
        console.log('PER-DOC CONTEXT UPDATES', perDocContextUpdates);
        await Promise.all(perDocContextUpdates.flatMap(({ ids: docIds, dataField, newContext }) =>
          docIds.map(id => service.update(id, {
            ...updateFirstStep,
            $set: {
              ...(updateFirstStep.$set || {}),
              [dataField]: newContext,
              updatedBy: signature
            }
          }))
        ));
      } else if (hasFirst) {
        console.log(updateFirstStep)
        updateFirstStep.$set = { ...updateFirstStep.$set, updatedBy: page.getLoggedUserSignature() };
        await Promise.all(ids.map(id => service.update(id, updateFirstStep)));
      }
      if(hasSecond) {
        console.log(updateSecondStep);
        updateSecondStep.$set = { ...updateSecondStep.$set, updatedBy: page.getLoggedUserSignature() };
        await Promise.all(ids.map(id => service.update(id, updateSecondStep)));
      }
      if(hasFirst || hasSecond || hasPerDoc) {
        page.selectNone();
        page.closeModal();
        onSave && onSave();
      }
    });
  };

  return <div>
    <table className={'table table-sm'}>
      <thead className={'bg-light'}>
      <tr>
        <th>Ids</th>
        { columns.map(col => <th key={col.dataField} className={'text-center'}>{col.dataField}</th>) }
      </tr>
      
      {contextColumns.length > 0 && <tr>
        <th></th>
        {columns.map(col => <th key={col.dataField} className={'text-left'+(!_.isEmpty(contextFilter) ? ' bg-light-warning' : '')}>
          {col.logic === LOGIC_LEGO_CONTEXT ? <div className={'d-flex align-items-center'}>
            <span className={'small text-muted'}>Apply to contexts matching:</span>
            <div className={'flex-grow-1 zoom-75'}>
              <ModelContextEditor value={contextFilter} onChange={setContextFilter}/>
            </div>
          </div> : null}
        </th>)}
      </tr>}

      <tr>
        <th>({docs.length} documents)</th>
        {columns.map(col => <th key={col.dataField}  className={'text-center'}>{col.valueEditor(fieldsChange[col.dataField], (val) => setFieldsChange({... fieldsChange, [col.dataField]: val}))}</th>)}
      </tr>
      </thead>

      <tbody>
      {predictChangedDocs.map(({ doc, rowHasDiff, colData }) =>
        <tr key={doc._id} className={rowHasDiff ? '' : 'translucent'}>
          <td>{doc.dataIssuesIds ? 'Cluster' : ''} <BadgeId id={doc._id}/></td>

          {columns.map(({ dataField, valueDisplay, logic }) => {
            const data = colData[dataField];
            if (logic === LOGIC_LEGO_CONTEXT) {
              return <td key={dataField}>
                {data.entries.map(({ contextPreview, className }, i) =>
                  <div key={i} className={className}>
                    <LegoContextSummary context={contextPreview}/>
                  </div>
                )}
              </td>;
            }
            return <td key={dataField} className={data.className}>
              {valueDisplay(data.predictedVal)}
            </td>;
          })}
        </tr>
      )}
      </tbody>
    </table>


    <span className={'btn btn-primary mt-4' + (affectedCount === 0 ? ' disabled' : '')} onClick={() => page.runAsync(createTasks)}>
      Update {affectedCount} doc{affectedCount === 1 ? '' : 's'}
    </span>

    <span className={'btn btn-light ml-1 mt-4'} onClick={() => setFieldsChange(_.cloneDeep(fieldsCommonFactor))}>
      Reset changes
    </span>
  </div>;
}
